import { InfoWidgetType, PlayerWidgetType, SmartLinkWidgetType } from './enums';
import { LinkType } from './linkTypes';
import type { SerializedMediaWidgetData } from './mediaWidget';
import type { SerializedPresaveSubscribeWidgetData } from './presaveSubscriptions';

/**
 * The wire contract for a public SmartLink page.
 *
 * Emitted by two services — the FYM server (from stored SmartLinkWidgetModels)
 * and musicapi (synthesised from track data for link.music24.com) — and
 * consumed by the sml-preview website. It lives here because websites may not
 * depend on fym-common, and because a contract with three consumers needs one
 * owner.
 *
 * Note that every widget carries resolved URLs, never database ids. The raw
 * stored widget data (fym-common's RawWidgetData) is a different, private shape.
 */

export type SerializedFile = {
  id: number;
  uuid: string;
  url: string;
};

/**
 * The page themes a SmartLink can wear. The order here is the order the theme
 * picker shows, so the four original themes stay first.
 *
 * Every value must also exist in the Postgres enum SmartLinkVersionTheme and
 * have a stylesheet at sml-preview/app/src/assets/styles/themes/<value>.scss.
 */
export enum SerializedSmartLinkVersionTheme {
  chill = 'chill',
  deep = 'deep',
  dark = 'dark',
  loli = 'loli',
  paper = 'paper',
  neon = 'neon',
  cassette = 'cassette',
  velvet = 'velvet',
  frost = 'frost',
  bloom = 'bloom',
  grid = 'grid',
  ember = 'ember',
  chrome = 'chrome',
  riso = 'riso',
}

/** How often an account uses one theme, for the "most used" row in the picker. */
export interface SmartLinkThemeUsage {
  theme: SerializedSmartLinkVersionTheme;
  count: number;
}

export interface SmartLinkThemeUsageResponse {
  /** Most used first. Themes the account has never used are absent. */
  themes: SmartLinkThemeUsage[];
}

export enum SerializedPublicAccountTrackerType {
  facebookPixel = 'facebookPixel',
  googleAnalyticsLegacy = 'googleAnalyticsLegacy',
  googleAnalytics = 'googleAnalytics',
  googleTagManager = 'googleTagManager',
  googleGlobalSite = 'googleGlobalSite',
  plausible = 'plausible',
}

export type SerializedAccountTrackerData = unknown;

export type SerializedPublicAccountTracker = {
  type: SerializedPublicAccountTrackerType;
  data: SerializedAccountTrackerData;
  /**
   * The vendor's own public identifier — GA measurement id, Meta pixel id,
   * Plausible domain. Safe to serialise publicly: every site using these ships
   * the same value in its client-side script. The tracker's `privateData`
   * (e.g. Plausible shared tokens) and `snippet` are deliberately not exposed.
   */
  trackerId: string;
};

export type SerializedPublicAccount = {
  id: number;
  name: string | null;
  avatarFile: SerializedFile | null;
  avatarProcessedUrl: string;
  slug: string | null;
  customDomain: string | null;
  /**
   * Which of the platform hosts this account's pages are advertised on. Null
   * means the default. A SmartLink may override it. Not `customDomain`, which
   * is a domain the artist owns.
   */
  platformDomain: string | null;
  shareUrl: string | null;
  mainSmartLinkId: number;
  /**
   * Show the "Made with Music24" footer. Absent on payloads cached before the
   * plans shipped; a renderer treats absent as true.
   */
  showMusic24Badge?: boolean;
};

/**
 * Where a public profile link on a SmartLink page comes from. The social links
 * block has no list of its own: it shows what the artist set up elsewhere.
 */
export enum ArtistProfileLinkSource {
  /** Catalog → Service connections: the artist's streaming profile url. */
  serviceConnection = 'serviceConnection',
  /** Promote → Channels: a social account, from its profile url or its handle. */
  socialChannel = 'socialChannel',
}

/** One public artist profile, as the social links block shows it. */
export type SerializedSocialLink = {
  url: string;
  type: LinkType;
  source: ArtistProfileLinkSource;
};

/**
 * What a pre-save block shows on a public page.
 *
 * The stored widget data holds only the campaign uuid and the services to
 * offer. The server joins the PresaveCampaignModel row on every serialisation,
 * so `released` and `releaseDate` are always current.
 */
export interface PresaveWidgetData {
  /** The pre-save campaign of the block. An empty string when the block has none ("Catalog links" of a song that is out). */
  campaignUuid: string;
  /**
   * False when the block has no pre-save campaign (a "Catalog links" block of a released song).
   * Absent on an answer of an older server: then the block has a campaign.
   */
  hasCampaign?: boolean;
  /** The catalog song of the block, when the server knows it. */
  catalogItemUuid?: string | null;
  /** ISO 8601 */
  releaseDate: string;
  /**
   * When the campaign was created, ISO 8601.
   *
   * The block draws a progress hairline under the countdown, and a bar needs a
   * start as well as an end. The campaign row's `createdAt` is the moment the
   * artist opened the pre-save, so it is the honest zero point.
   */
  campaignStartedAt: string;
  trackTitle: string;
  artistName: string;
  /** MusicAPIIntegrationID values to render as buttons */
  services: string[];
  /**
   * False when the deployment cannot start a streaming-service OAuth run —
   * `MUSICAPI_MUSIC24_CLIENT_TOKEN` is unset, so `/public/presave/…/auth/…`
   * answers 503 for every service.
   *
   * A full-page redirect cannot report that status back to the page, so the
   * public block must know before it draws a button. `services` still carries
   * the artist's own choice, so an artist-facing preview can say why the
   * buttons are missing instead of pretending the campaign never had any.
   */
  serviceAuthEnabled: boolean;
  released: boolean;
  /**
   * True when the campaign expired or was cancelled, and for a widget whose
   * campaign row is gone.
   *
   * The server refuses every sign-up on such a campaign, so a page that still
   * drew a countdown and a form would collect nothing but 410s. Separate from
   * `released`: that one means the track is out and the links are on the page,
   * this one means the offer ended without one.
   */
  closed: boolean;
  /**
   * The links of the released song, keyed by `MusicAPIIntegrationID`: the current service links of
   * the catalog song (an artist's or a distributor's link wins over a guess), and the urls of the
   * pre-save release where the catalog has none. Read at render time, so a link that the catalog
   * finds after the release shows without a republish. Only serialised once the song is out.
   */
  resolvedUrls?: Record<string, string>;
  /**
   * How many fans signed up, serialised only once the campaign is released.
   *
   * Every subscription row on the campaign except the ones a fan withdrew
   * (`unsubscribed`). Sign-ups, not deliveries: a fan whose save later failed
   * or whose token expired still chose to pre-save, and the sentence the block
   * shows is about the crowd, not about our job queue.
   */
  presaveCount?: number;
  /**
   * ISO time when the Bio Link auto add put the block on the page (fym-shared
   * `smartlinks/bioLinkSettings.ts`). Absent on a block that the artist added. The editor shows a
   * "New" tag for 7 days.
   */
  autoAddedAt?: string;
}

export type SerializedWidgetData = {
  [SmartLinkWidgetType.info]: { text: string; type: InfoWidgetType };
  [SmartLinkWidgetType.image]: {
    // Nullable: an auto-generated link has no FileModel row behind its cover
    // art. Renderers read imageProcessedUrl and never touch this.
    image: SerializedFile | null;
    imageProcessedUrl: string | null;
  };
  [SmartLinkWidgetType.link]: {
    url: string;
    label: string | null;
    type: LinkType;
    isMusicService: boolean;
    smartLinkId: number | undefined;
  };
  [SmartLinkWidgetType.socialLinks]: {
    /**
     * The profiles that fans see, in the artist's order, with the profiles the artist turned off
     * left out. A renderer shows this list as it is.
     */
    socialLinks: SerializedSocialLink[];
    /**
     * Owner answers only (the editor): every profile of the artist in the artist's order, also the
     * ones that are off. Absent on the public page answer and on an older server.
     */
    allSocialLinks?: SerializedSocialLink[];
    /** Owner answers only: the stored order (link types). Types not in it follow, in the default order. */
    order?: LinkType[];
    /** Owner answers only: the link types that the artist turned off on this block. */
    hiddenTypes?: LinkType[];
  };
  [SmartLinkWidgetType.profile]: Record<string, never>;
  [SmartLinkWidgetType.player]: {
    url: string;
    embedUrl?: string;
    type?: PlayerWidgetType;
    musicServiceType: LinkType;
    version?: number;
    height?: number;
  };
  [SmartLinkWidgetType.presave]: PresaveWidgetData;
  [SmartLinkWidgetType.media]: SerializedMediaWidgetData;
  [SmartLinkWidgetType.presaveSubscribe]: SerializedPresaveSubscribeWidgetData;
};

type GenericSerializedWidget<T extends SmartLinkWidgetType> = {
  uuid: string;
  position: number;
  type: T;
  data: SerializedWidgetData[T];
  /**
   * True when the artist hid the block on the page (the eye in the editor, 2026-10-20). The block
   * stays in the editor. The public page answer never holds a hidden block, so only the editor
   * (and its preview) sees the flag. Absent = false (an older server, a public answer).
   */
  hidden?: boolean;
};

export type SerializedInfoWidget = GenericSerializedWidget<SmartLinkWidgetType.info>;

export type SerializedImageWidget = GenericSerializedWidget<SmartLinkWidgetType.image>;

export type SerializedLinkWidget = GenericSerializedWidget<SmartLinkWidgetType.link>;

export type SerializedSocialLinksWidget = GenericSerializedWidget<SmartLinkWidgetType.socialLinks>;

export type SerializedPlayerWidget = GenericSerializedWidget<SmartLinkWidgetType.player>;

export type SerializedProfileWidget = GenericSerializedWidget<SmartLinkWidgetType.profile>;

export type SerializedPresaveWidget = GenericSerializedWidget<SmartLinkWidgetType.presave>;
export type SerializedPresaveSubscribeWidget = GenericSerializedWidget<SmartLinkWidgetType.presaveSubscribe>;

export type SerializedMediaWidget = GenericSerializedWidget<SmartLinkWidgetType.media>;

export type SerializedWidget =
  | SerializedInfoWidget
  | SerializedImageWidget
  | SerializedLinkWidget
  | SerializedSocialLinksWidget
  | SerializedProfileWidget
  | SerializedPlayerWidget
  | SerializedPresaveWidget
  | SerializedPresaveSubscribeWidget
  | SerializedMediaWidget;

/**
 * The song of a song SmartLink (design `asset-redesign/m Link preview - new`, owner answers
 * 2026-10-08): the drawn share image of a page with no kit link preview shows the same layout
 * ("New single", the title, the artist, the cover and the status badge, Clean theme).
 */
export type SerializedSmartLinkRelease = {
  title: string;
  artist: string;
  /** `YYYY-MM-DD`; null when the song has no release date. */
  releaseDate: string | null;
  /** True when the song has a published pre-save page. */
  hasPresave: boolean;
  /** The absolute URL of the cover; null when the song has none. */
  artworkUrl: string | null;
  /** The palette that the release theme builds its colours from (hex); can be empty. */
  palette: string[];
};

export type SerializedPublicSmartLinkVersion = {
  name: string;
  metaTitle: string | null;
  metaDescription: string | null;
  slug: string;
  backgroundFile: SerializedFile | null;
  backgroundProcessedUrl: string;
  widgets: SerializedWidget[] | null;
  theme: SerializedSmartLinkVersionTheme;
  /**
   * The absolute URL of the release kit's link preview image (1200 x 630), the page's
   * share image. Null when the song has no ready link preview.
   */
  previewImageUrl: string | null;
  /**
   * The song of the SmartLink: the pre-save campaign's song, or the catalog song with the
   * SmartLink's catalog track. Absent or null for a SmartLink of no song (an older API answer has
   * no field).
   */
  release?: SerializedSmartLinkRelease | null;
};

type SerializedPublicSmartLinkBase = {
  id: number;
  accountId: number;
  shareUrl: string | null;
  embedUrl: string | null;
  /**
   * Signed page context the public page sends back with each analytics event.
   * The ingest endpoint is anonymous, so this is what stops a page from
   * attributing traffic to a SmartLink it does not own. Null in preview mode,
   * where events are not recorded.
   */
  analyticsToken: string | null;
};

export interface SerializedPublicSmartLinkPublished extends SerializedPublicSmartLinkBase {
  publishedSmartLinkVersion: SerializedPublicSmartLinkVersion;
  recentSmartLinkVersion: null;
}

export interface SerializedPublicSmartLinkPreview extends SerializedPublicSmartLinkBase {
  recentSmartLinkVersion: SerializedPublicSmartLinkVersion;
  publishedSmartLinkVersion: null;
}

export type SerializedPublicSmartLink = SerializedPublicSmartLinkPublished | SerializedPublicSmartLinkPreview;

export interface PublicAccountSmartLinkAPIResponse {
  account: SerializedPublicAccount;
  socialLinks: SerializedSocialLink[];
  smartLink: SerializedPublicSmartLink;
  accountTrackers: SerializedPublicAccountTracker[];
}
